import { useGoogleMap } from '@ubilabs/google-maps-react-hooks';
import _ from 'lodash';
import { useEffect } from 'react';

import { useClinics } from '../hooks/useClinics';
import {
  clinicsWithDuplicateCoordinatesSelector,
  clinicsWithMatchingCoordinatesSelector,
  sortedClinicsWithDistance,
} from '../store/selectors';
import { useLocatorStore } from '../store/useLocatorStore';

import { useAddClinicsToLocalStorage } from './hooks/useAddClinicsToLocalStorage';
import { useHandleBounds } from './hooks/useHandleBounds';
import { useHandleGeocodeCurrentLocation } from './hooks/useHandleGeocodeCurrentLocation';
import { useHandleGeocodeSearchInput } from './hooks/useHandleGeocodeSearchInput';
import { useHandleNoResults } from './hooks/useHandleNoResults';
import { ClinicMarker } from './Map.ClinicMarker';
import { LocationMarker } from './Map.LocationMarker';
import { MarkerWrapper } from './Map.MarkerWrapper';

type Props = {
  mapRef: any;
  className?: string;
};

export function Map({ mapRef, className = '' }: Props) {
  return (
    <div ref={mapRef} className={`min-h-[450px] md:h-full ${className}`}>
      <MapMarkers />
    </div>
  );
}

function MapMarkers() {
  const map = useGoogleMap();
  // Flatten the array of groups to get the clinics with duplicate coordinates
  const { clinics } = useClinics();
  const allClinics = useLocatorStore((state) => sortedClinicsWithDistance(state, clinics));
  const visibleClinics = useLocatorStore((state) =>
    sortedClinicsWithDistance(state, state.visibleClinics)
  );
  const clinicsWithDuplicateCoordinates = clinicsWithDuplicateCoordinatesSelector(allClinics);

  const {
    setVisibleClinics,
    setRadius,
    currentLocation,
    closestClinic,
    setActiveClinic,
    activeClinic,
    setClosestClinic,
    setSidebarOpen,
    isUserZooming,
    shouldGeoLocate,
    setSortClinicsBy,
  } = useLocatorStore((state) => state);

  useEffect(() => {
    if (!map || !isUserZooming) return;

    const handleBoundsChange = _.debounce(() => {
      setClosestClinic(undefined);
      setRadius(undefined);
      setVisibleClinicMarkers();
    }, 100);

    const boundsListener = map?.addListener('bounds_changed', () => {
      handleBoundsChange();
    });

    map?.addListener('drag', () => {
      // @ts-expect-error
      if (window.dataLayer) {
        // @ts-expect-error
        window.dataLayer.push({
          'event': 'V3_clinic_finder_map_drag',
        });
      }
    });

    map.addListener('zoom_changed', () => {
      // @ts-expect-error
      if (window.dataLayer) {
        // @ts-expect-error
        window.dataLayer.push({
          'event': 'V3_clinic_finder_map_zoom',
        });
      }
    });

    return () => boundsListener?.remove();
  }, [map, isUserZooming]);

  useEffect(() => {
    // @ts-expect-error
    setActiveClinic(undefined);
  }, [shouldGeoLocate]);

  function setVisibleClinicMarkers() {
    const bounds = map?.getBounds();

    const newVisibleClinics = allClinics.filter((clinic) => {
      const clinicLocation = {
        lat: clinic.Geolocation__c.latitude,
        lng: clinic.Geolocation__c.longitude,
      };

      return bounds?.contains(clinicLocation);
    });

    setVisibleClinics(newVisibleClinics);
  }

  useHandleBounds();
  useHandleGeocodeSearchInput();
  useHandleGeocodeCurrentLocation();
  useHandleNoResults();
  useAddClinicsToLocalStorage();

  const clinicsWithMatchingCoordinates = clinicsWithMatchingCoordinatesSelector(
    activeClinic,
    visibleClinics
  );

  return (
    <>
      {currentLocation && (
        <MarkerWrapper map={map} position={currentLocation} onClick={() => {}}>
          <LocationMarker />
        </MarkerWrapper>
      )}
      {closestClinic && (
        <MarkerWrapper
          map={map}
          key={closestClinic.API_Id__c}
          position={{
            lat: closestClinic.Geolocation__c.latitude,
            lng: closestClinic.Geolocation__c.longitude,
          }}
          onClick={() => {
            setSidebarOpen(true);
            setActiveClinic(closestClinic);
          }}
        >
          {/* @ts-expect-error */}
          <ClinicMarker
            level={closestClinic.partnerLevel}
            isActive={activeClinic?.API_Id__c === closestClinic.API_Id__c}
          />
        </MarkerWrapper>
      )}
      {visibleClinics.map((clinic) => {
        const matchingClinicCount = clinicsWithDuplicateCoordinates.filter(
          (c) =>
            c.Geolocation__c.latitude === clinic.Geolocation__c.latitude &&
            c.Geolocation__c.longitude === clinic.Geolocation__c.longitude
        ).length;

        return (
          <MarkerWrapper
            map={map}
            key={clinic.API_Id__c}
            position={{
              lat: clinic.Geolocation__c.latitude,
              lng: clinic.Geolocation__c.longitude,
            }}
            isActive={activeClinic?.API_Id__c === clinic.API_Id__c}
            onClick={() => {
              if (matchingClinicCount > 1) {
                setSortClinicsBy('distance');
              }
              setSidebarOpen(true);
              setActiveClinic(clinic);
              // @ts-expect-error
              if (window.dataLayer) {
                // @ts-expect-error
                window.dataLayer.push({
                  'clinic_ID': clinic.Id,
                  'clinic_Name': clinic.Name,
                  'event': 'Clinic Pin Click',
                });
              }
            }}
          >
            <ClinicMarker
              level={clinic.partnerLevel}
              isActive={
                activeClinic?.API_Id__c === clinic.API_Id__c ||
                clinicsWithMatchingCoordinates.includes(clinic)
              }
              matchingClinicCount={matchingClinicCount}
            />
          </MarkerWrapper>
        );
      })}
    </>
  );
}
